const analyticsEndpoint = () => document.querySelector('meta[name="analytics-endpoint"]')?.content || '/analytics/event';

const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

const normalizePath = (pathValue) => {
    const fallbackPath = window.location.pathname;
    const rawPath = String(pathValue || fallbackPath).trim();
    if (rawPath === '') {
        return fallbackPath;
    }

    try {
        const url = new URL(rawPath, window.location.origin);
        return url.pathname || fallbackPath;
    } catch (error) {
        return rawPath.startsWith('/') ? rawPath : fallbackPath;
    }
};

const buildPayload = (eventName, metadata = {}) => ({
    event_name: eventName,
    path: normalizePath(metadata.path),
    referrer: metadata.referrer || null,
    calculator_key: metadata.calculatorKey || null,
    cta_key: metadata.ctaKey || null,
    utm_source: metadata.utmSource || null,
    utm_medium: metadata.utmMedium || null,
    utm_campaign: metadata.utmCampaign || null,
});

const trackGoogleAnalyticsEvent = (eventName, payload) => {
    if (!['calculator_started', 'calculation_completed', 'cta_clicked'].includes(eventName)
        || !window.calcimoConsent?.hasAnalyticsConsent() || typeof window.gtag !== 'function') {
        return;
    }

    const parameters = {};
    if (payload.calculator_key) {
        parameters.calculator_key = payload.calculator_key;
    }
    if (payload.cta_key) {
        parameters.cta_key = payload.cta_key;
    }

    window.gtag('event', eventName, parameters);
};

/**
 * Read only the campaign parameters of the landing URL. Click identifiers such as
 * gclid or fbclid are deliberately ignored and nothing is persisted in the browser.
 */
const readUtmParameters = () => {
    const params = new URLSearchParams(window.location.search);

    return {
        utmSource: params.get('utm_source'),
        utmMedium: params.get('utm_medium'),
        utmCampaign: params.get('utm_campaign'),
    };
};

export const trackAnalyticsEvent = async (eventName, metadata = {}) => {
    const payload = buildPayload(eventName, metadata);
    const csrfToken = getCsrfToken();

    trackGoogleAnalyticsEvent(eventName, payload);

    try {
        const response = await fetch(analyticsEndpoint(), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
            },
            body: JSON.stringify(payload),
            keepalive: true,
        });
        if (!response.ok && window.console && typeof window.console.warn === 'function') {
            console.warn('Analytics event was not accepted by backend.', eventName, response.status);
        }
    } catch (error) {
        // Tracking must never block user interactions.
    }
};

export const trackCalculatorStarted = (calculatorKey, metadata = {}) => {
    return trackAnalyticsEvent('calculator_started', {
        ...metadata,
        calculatorKey,
    });
};

export const trackCalculationCompleted = (calculatorKey, metadata = {}) => {
    return trackAnalyticsEvent('calculation_completed', {
        ...metadata,
        calculatorKey,
    });
};

export const trackCtaClicked = (ctaKey, metadata = {}) => {
    return trackAnalyticsEvent('cta_clicked', {
        ...metadata,
        ctaKey,
    });
};

export const initAnalyticsTracking = () => {
    const completedCalculator = document.querySelector('meta[name="analytics-completed-calculator"]')?.content;
    if (completedCalculator) {
        trackGoogleAnalyticsEvent('calculation_completed', { calculator_key: completedCalculator });
    }

    const pageView = trackAnalyticsEvent('page_view', {
        referrer: document.referrer,
        ...readUtmParameters(),
    });

    document.querySelectorAll('[data-analytics-cta]').forEach((element) => {
        element.addEventListener('click', () => {
            const ctaKey = element.getAttribute('data-analytics-cta');
            const calculatorKey = element.getAttribute('data-analytics-calculator');

            trackCtaClicked(ctaKey || 'unknown', {
                calculatorKey: calculatorKey || null,
            });
        });
    });

    return pageView;
};
